import { Alert, Box, Button, Portal, Snackbar, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";
import AddSeasonDialog from "./components/AddSeasonDialog";
import FinishSeasonDialog from "./components/FinishSeasonDialog";
import ManageRankDialog from "./components/ManageRankDialog";
import NewRoundDialog from "./components/NewRoundDialog";
import SeasonAccordion from "./components/SeasonAccordion";
import useAdminMatches from "./hooks/useAdminMatches";

const Matches = () => {
  const {
    seasons,
    categories,
    lineDrafts,
    sentLines,
    addSeasonOpen,
    newSeasonName,
    finishModal,
    newRoundModal,
    manageRankModal,
    manageRankSearch,
    selectedSeasonForFinish,
    selectedSeasonForNewRound,
    selectedSeasonForManageRank,
    setAddSeasonOpen,
    setNewSeasonName,
    setFinishModal,
    setNewRoundModal,
    setManageRankSearch,
    getSeasonView,
    updateSeasonUi,
    handleToggleSeason,
    handleScoreChange,
    lineHasChanges,
    canSendLine,
    handleSendLine,
    handleAddSeason,
    handleFinishSeason,
    handleCreateNewRound,
    handleOpenManageRank,
    handleCloseManageRank,
    handleManageRankCategoryChange,
    handleAddUserToRank,
    handleRemoveUserFromRank,
    getManageRankView,
  } = useAdminMatches();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSendLineWithFeedback = async (lineId, line) => {
    console.log("Sending line", lineId, line);
    try {
      const result = await handleSendLine(lineId, line);
      console.log("result", result);
      if (result?.error) {
        setSnackbar({ open: true, message: result.error, severity: "error" });
      }
    } catch (error) {
      console.log("result error", error);
      setSnackbar({
        open: true,
        message: error?.message || "Erro ao enviar resultados.",
        severity: "error",
      });
    }
  };

  const manageRankView = getManageRankView();
  const manageRankSeasonId = manageRankView.seasonId;

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
            Rodadas
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Gerencie rodadas, resultados e rankings da competição.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddSeasonOpen(true)}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Criar Ranking
        </Button>
      </Stack>

      <Stack spacing={1.5}>
        {seasons.map((season) => {
          const { ui, seasonResult, lines, membersCount, rounds } =
            getSeasonView(season);

          return (
            <SeasonAccordion
              key={season.id}
              season={season}
              ui={ui}
              seasonResult={seasonResult}
              lines={lines}
              membersCount={membersCount}
              sentLines={sentLines}
              lineDrafts={lineDrafts}
              categories={categories}
              rounds={rounds}
              onToggleSeason={handleToggleSeason}
              onUpdateSeasonUi={updateSeasonUi}
              onOpenFinishSeason={(seasonId) =>
                setFinishModal({ open: true, seasonId })
              }
              onOpenNewRound={(seasonId) =>
                setNewRoundModal({ open: true, seasonId, status: null })
              }
              onOpenManageRank={handleOpenManageRank}
              onScoreChange={handleScoreChange}
              lineHasChanges={lineHasChanges}
              canSendLine={canSendLine}
              onSendLine={handleSendLineWithFeedback}
            />
          );
        })}
      </Stack>

      <ManageRankDialog
        open={manageRankModal.open}
        seasonName={selectedSeasonForManageRank?.name}
        categories={categories}
        selectedCategory={manageRankModal.selectedCategory}
        onChangeCategory={handleManageRankCategoryChange}
        search={manageRankSearch}
        onChangeSearch={setManageRankSearch}
        usersInRank={manageRankView.usersInRank}
        usersOutRank={manageRankView.usersOutRank}
        onRemove={(userId) =>
          manageRankSeasonId &&
          handleRemoveUserFromRank(manageRankSeasonId, userId)
        }
        onAdd={(userId) =>
          manageRankSeasonId && handleAddUserToRank(manageRankSeasonId, userId)
        }
        onClose={handleCloseManageRank}
      />

      <AddSeasonDialog
        open={addSeasonOpen}
        seasonName={newSeasonName}
        onChangeSeasonName={setNewSeasonName}
        onClose={() => setAddSeasonOpen(false)}
        onConfirm={handleAddSeason}
      />

      <FinishSeasonDialog
        open={finishModal.open}
        seasonName={selectedSeasonForFinish?.name}
        onClose={() => setFinishModal({ open: false, seasonId: null })}
        onConfirm={handleFinishSeason}
      />

      <NewRoundDialog
        open={newRoundModal.open}
        seasonName={selectedSeasonForNewRound?.name}
        status={newRoundModal.status}
        errorMessage={newRoundModal.errorMessage}
        endsAt={newRoundModal.endsAt}
        onEndsAtChange={(value) =>
          setNewRoundModal((prev) => ({ ...prev, endsAt: value }))
        }
        onClose={() =>
          setNewRoundModal({
            open: false,
            seasonId: null,
            status: null,
            errorMessage: null,
            endsAt: null,
          })
        }
        onConfirm={handleCreateNewRound}
      />

      <Portal>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={(_, reason) => {
            if (reason === "clickaway") return;
            setSnackbar((prev) => ({ ...prev, open: false }));
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            sx={{ maxWidth: 520 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Portal>
    </Stack>
  );
};

export default Matches;
